import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface Profile {
  id: string;
  user_id: string;
  name: string;
  email: string;
  role: 'provider' | 'case_manager' | 'social_worker' | 'discharge_planner' | 'housing_stabilization_worker' | 'admin' | 'hss_provider';
  organization?: string;
  npi?: string;
  subscription_status: 'active' | 'inactive' | 'trial';
  created_at: string;
  updated_at: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  profile_picture_url?: string;
  bio?: string;
  license_number?: string;
  provider_type?: 'assisted_living' | 'hss_agency';
  agency_name?: string;
  counties_served?: string[];
  populations_served?: string[];
  languages_spoken?: string[];
  accepting_clients?: boolean;
  umpi?: string;
  preferred_contact_method?: 'email' | 'fax';
}

interface SubscriptionData {
  subscribed: boolean;
  subscription_tier: string | null;
  subscription_end: string | null;
  location_limit: number;
  payment_status: string;
  dashboard_access_suspended: boolean;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  subscription: SubscriptionData | null;
  subscriptionLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, userData: { name: string; role: string; organization?: string; npi?: string }) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (userData: Partial<Profile>) => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  useEffect(() => {
    console.log('AuthContext: Setting up auth state listener');
    
    // Set timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      console.log('AuthContext: Timeout reached, setting loading to false');
      setLoading(false);
    }, 10000);

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('AuthContext: Auth state changed', { event, hasSession: !!session });
        clearTimeout(timeout);
        
        setUser(session?.user ?? null);
        
        if (session?.user) {
          console.log('AuthContext: Fetching profile for user', session.user.id);
          // Defer profile fetch to avoid deadlock
          setTimeout(async () => {
            try {
              const { data: profileData, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('user_id', session.user.id)
                .maybeSingle();
              
              if (error) {
                console.error('AuthContext: Profile fetch error', error);
                setProfile(null);
              } else {
                console.log('AuthContext: Profile fetched', profileData);
                setProfile(profileData as Profile);
              }
              
              // Fetch subscription data
              await fetchSubscriptionData(session.user.id);
            } catch (err) {
              console.error('AuthContext: Profile fetch exception', err);
              setProfile(null);
            }
            setLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setSubscription(null);
          setLoading(false);
        }
      }
    );

    // Get initial session
    console.log('AuthContext: Getting initial session');
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('AuthContext: Get session error', error);
        setLoading(false);
        return;
      }
      
      console.log('AuthContext: Initial session', { hasSession: !!session });
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setTimeout(async () => {
          try {
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('*')
              .eq('user_id', session.user.id)
              .maybeSingle();
            
            if (profileError) {
              console.error('AuthContext: Initial profile fetch error', profileError);
              setProfile(null);
            } else {
              console.log('AuthContext: Initial profile fetched', profileData);
              setProfile(profileData as Profile);
            }
            
            // Fetch subscription data
            await fetchSubscriptionData(session.user.id);
          } catch (err) {
            console.error('AuthContext: Initial profile fetch exception', err);
            setProfile(null);
          }
          setLoading(false);
        }, 0);
      } else {
        setLoading(false);
      }
    }).catch((err) => {
      console.error('AuthContext: Get session exception', err);
      setLoading(false);
    });

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const fetchSubscriptionData = async (userId: string) => {
    if (!userId) return;
    
    try {
      setSubscriptionLoading(true);
      
      // First, check subscription status via edge function
      const { data: checkData, error: checkError } = await supabase.functions.invoke('check-subscription');
      
      if (checkError) {
        console.error('Error checking subscription:', checkError);
        return;
      }

      // Then fetch subscriber data from database
      const { data: subscriberData, error: subscriberError } = await supabase
        .from('subscribers')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (subscriberError && subscriberError.code !== 'PGRST116') {
        console.error('Error fetching subscriber data:', subscriberError);
        return;
      }

      if (subscriberData) {
        setSubscription({
          subscribed: subscriberData.subscribed,
          subscription_tier: subscriberData.subscription_tier,
          subscription_end: subscriberData.subscription_end,
          location_limit: subscriberData.location_limit,
          payment_status: subscriberData.payment_status,
          dashboard_access_suspended: subscriberData.dashboard_access_suspended,
        });
      } else {
        // Default subscription data for users without a subscription
        setSubscription({
          subscribed: false,
          subscription_tier: null,
          subscription_end: null,
          location_limit: 0,
          payment_status: 'inactive',
          dashboard_access_suspended: false,
        });
      }
    } catch (error) {
      console.error('Error in fetchSubscriptionData:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const refreshSubscription = async () => {
    if (user?.id) {
      await fetchSubscriptionData(user.id);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
  };

  const signUp = async (email: string, password: string, userData: { name: string; role: string; organization?: string; npi?: string }) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          name: userData.name,
          role: userData.role,
          organization: userData.organization,
          npi: userData.npi,
        }
      }
    });
    
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setSubscription(null);
  };

  const updateProfile = async (userData: Partial<Profile>) => {
    if (!user) return;
    
    const { error } = await supabase
      .from('profiles')
      .update(userData)
      .eq('user_id', user.id);
    
    if (error) throw error;
    
    // Update local state
    if (profile) {
      setProfile({ ...profile, ...userData });
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      profile, 
      loading, 
      subscription, 
      subscriptionLoading, 
      signIn, 
      signUp, 
      signOut, 
      updateProfile, 
      refreshSubscription 
    }}>
      {children}
    </AuthContext.Provider>
  );
};